import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Upload, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PublicApply
 * Reused by /driver-signup, /partner-signup, /franchisee/signup.
 * Posts to /api/applications/{role} which writes to `public_applications`
 * collection and pings the admin audit log. No auth required; admin
 * reviews from /admin/supplies/applications.
 */
// Iter60 Task 1 — business category selector. Required for partner.
export const PARTNER_BUSINESS_CATEGORIES = [
  { value: "product_brand", label: "Product Brand" },
  { value: "store",         label: "Store" },
  { value: "dispensary",    label: "Dispensary" },
  { value: "glassblower",   label: "Glassblower / Glass Shop" },
  { value: "vape_store",    label: "Vape-Specific Store" },
  { value: "artist",        label: "Artist" },
  { value: "other",         label: "Other Business" },
];

export default function PublicApply({ role, title, blurb, extraFields = [], roleTestId, defaultBusinessCategory }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    notes: "",
    business_name: "",
    business_category: defaultBusinessCategory || "",
    logo_url: "",
    password: "",
    password_confirm: "",
  });
  const [extra, setExtra] = useState(() => {
    // Section 2 — apply any `defaultValue` from extra fields (used by
    // dedicated signup pages to seed `partner_type` etc.)
    const seed = {};
    for (const f of extraFields || []) {
      if (f.defaultValue !== undefined) seed[f.key] = f.defaultValue;
    }
    return seed;
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  function setField(key) {
    return (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  }
  function setExtraField(key) {
    return (e) => setExtra((x) => ({ ...x, [key]: e.target.value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    // Iter60 Task 1 — frontend guard for partner business category
    if (role === "partner" && !form.business_category) {
      setError("Please select a business category.");
      return;
    }
    // Iter72 — password validation (required, min 8, must confirm)
    if (!form.password) {
      setError("Please choose a password for your customer account.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.password_confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { password_confirm, ...submitForm } = form;
      const r = await fetch(`${API}/api/applications/${role}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role, ...submitForm, payload: extra }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setError(body.detail || "Could not submit. Please try again.");
      } else {
        setResult(await r.json());
      }
    } catch {
      setError("Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-md" data-testid={`${roleTestId}-success`}>
        <div className="glass-panel p-6 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-lifted-green/20 flex items-center justify-center mb-4">
            <CheckCircle2 className="text-lifted-green" size={24} />
          </div>
          <h1 className="font-anton uppercase text-2xl text-white mb-2">
            Application received
          </h1>
          <p className="text-lifted-dim text-sm mb-2">
            Reference: <code className="text-lifted-green">{result.id}</code>
          </p>
          <p className="text-lifted-dim text-sm mb-6">
            Lifted admin reviews every application personally. You'll hear back at{" "}
            <span className="text-white">{form.email}</span> with next steps.
          </p>
          {/* Iter67/72 — confirm customer account creation, password mode aware */}
          {result.customer_account_created && (
            <div className="text-xs text-lifted-dim bg-white/5 border border-white/10 rounded-md px-3 py-2 mb-4 text-left">
              <div className="text-lifted-green font-semibold uppercase tracking-wider text-[10px] mb-1">
                Customer account created
              </div>
              {result.user_chose_password ? (
                <>You can sign in now at <span className="text-white">/login</span> with{" "}
                <span className="text-white">{form.email}</span> and the password you just chose.{" "}
                Your role dashboard unlocks the moment admin approves.</>
              ) : (
                <>We sent a temporary password to <span className="text-white">{form.email}</span>.{" "}
                You can sign in to track your application status. Your role dashboard unlocks the moment admin approves.</>
              )}
            </div>
          )}
          {result.customer_account_created === false && (
            <div className="text-xs text-lifted-dim bg-white/5 border border-white/10 rounded-md px-3 py-2 mb-4 text-left">
              <div className="text-lifted-green font-semibold uppercase tracking-wider text-[10px] mb-1">
                Existing customer linked
              </div>
              We linked this application to your existing Lifted account ({form.email}).
              Sign in with your existing password.
            </div>
          )}
          <Link to="/" className="text-lifted-green underline text-sm">
            Back to LiftedAtTheMoment.com
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-10 lg:py-14 max-w-xl" data-testid={`${roleTestId}-page`}>
      <div className="glass-panel p-6 lg:p-8">
        <div className="text-xs uppercase tracking-[0.2em] text-lifted-green mb-2">
          Lifted Ecosystem · Apply
        </div>
        <h1 className="font-anton uppercase text-3xl text-white">{title}</h1>
        <p className="text-lifted-dim text-sm mt-2 mb-6">{blurb}</p>

        <form onSubmit={submit} className="space-y-3">
          <Field label="Full name" value={form.name} onChange={setField("name")} testId={`${roleTestId}-name`} required />
          <Field label="Email" type="email" value={form.email} onChange={setField("email")} testId={`${roleTestId}-email`} required />
          <Field label="Phone" value={form.phone} onChange={setField("phone")} testId={`${roleTestId}-phone`} required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="City" value={form.city} onChange={setField("city")} testId={`${roleTestId}-city`} />
            <Field label="State" value={form.state} onChange={setField("state")} testId={`${roleTestId}-state`} />
          </div>

          {/* Iter72 — Customer account password (required). Applicant becomes
              a customer at submit; same login carries forward after approval. */}
          <div className="rounded-xl bg-lifted-green/5 border border-lifted-green/20 p-3 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-lifted-green font-bebas">
              Create your customer login
            </div>
            <p className="text-[11px] text-lifted-dim">
              You'll sign in as a customer immediately so you can track your application.
              When your {role} application is approved, this <strong>same login</strong> upgrades to the
              {' '}<strong>{role === "franchisee" ? "franchise" : role}</strong> dashboard automatically.
              If you already have a Lifted account at this email, leave this field anything — it won't change your existing password.
            </p>
            <Field
              label="Password (min 8 chars)"
              type="password"
              value={form.password}
              onChange={setField("password")}
              testId={`${roleTestId}-password`}
              required
            />
            <Field
              label="Confirm password"
              type="password"
              value={form.password_confirm}
              onChange={setField("password_confirm")}
              testId={`${roleTestId}-password-confirm`}
              required
            />
          </div>

          {/* Iter60 Task 1 — partner-only business category + business name */}
          {role === "partner" && (
            <>
              <Field
                label="Business name"
                value={form.business_name}
                onChange={setField("business_name")}
                testId={`${roleTestId}-business-name`}
                placeholder="e.g. Acme Smoke Shop"
                required
              />
              <div>
                <label className="block text-xs uppercase tracking-wider text-lifted-dim mb-1">
                  Business category
                  <span className="text-lifted-green ml-1">*</span>
                </label>
                <select
                  value={form.business_category}
                  onChange={setField("business_category")}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:border-lifted-green"
                  data-testid={`${roleTestId}-business-category`}
                >
                  <option value="">Select your business type…</option>
                  {PARTNER_BUSINESS_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-lifted-dim mt-1">
                  If you're a product brand only, choose <span className="text-white">Product Brand</span>.
                </p>
              </div>
            </>
          )}

          {extraFields.filter((ef) => !ef.hidden).map((ef) => (
            <Field
              key={ef.key}
              label={ef.label}
              value={extra[ef.key] || ""}
              onChange={setExtraField(ef.key)}
              testId={`${roleTestId}-${ef.key}`}
              placeholder={ef.placeholder}
            />
          ))}

          {/* Iter67/71 — Store logo: real file upload OR URL paste fallback */}
          {(role === "partner" || role === "franchisee") && (
            <LogoUploader
              testId={`${roleTestId}-logo`}
              value={form.logo_url}
              onChange={(v) => setForm({ ...form, logo_url: v })}
            />
          )}
          <div>
            <label className="block text-xs uppercase tracking-wider text-lifted-dim mb-1">
              Anything we should know?
            </label>
            <textarea
              value={form.notes}
              onChange={setField("notes")}
              rows={4}
              className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white"
              data-testid={`${roleTestId}-notes`}
            />
          </div>
          {error && (
            <div className="text-sm text-red-300 bg-red-900/20 border border-red-500/30 px-3 py-2 rounded">
              {error}
            </div>
          )}
          <p className="text-xs text-lifted-dim leading-relaxed pt-1">
            By submitting, you acknowledge that approval requires admin review, identification
            verification (for drivers), and acceptance of the role-specific agreement. No
            guarantees of acceptance, earnings, or timeline.
          </p>
          <button
            type="submit"
            disabled={submitting}
            className="btn-lifted w-full disabled:opacity-50"
            data-testid={`${roleTestId}-submit`}
          >
            {submitting ? "Submitting…" : "Submit application"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-lifted-dim mb-1">
        {label}
        {required && <span className="text-lifted-green ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:border-lifted-green"
        data-testid={testId}
      />
    </div>
  );
}

// Iter71 — Real file uploader for partner / franchise application logo.
function LogoUploader({ value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showUrlInput, setShowUrlInput] = useState(false);

  const upload = async (file) => {
    setErr("");
    if (!file) return;
    if (!/^image\//.test(file.type)) { setErr("Pick an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("Image must be under 5 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/public/application-logo`, { method: "POST", body: fd });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Upload failed (${res.status}): ${t.slice(0, 200)}`);
      }
      const data = await res.json();
      onChange(`${API}${data.url}`);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs uppercase tracking-wider text-white/60">
        Store / brand logo
      </label>

      {value && (
        <div className="relative w-32 h-32 rounded-xl overflow-hidden bg-black/40 border border-white/10">
          <img
            src={value}
            alt="logo preview"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-1 right-1 bg-black/70 rounded-full p-1 hover:bg-rose-500/70"
            data-testid={`${testId}-clear`}
            aria-label="Remove logo"
          >
            <X className="h-3.5 w-3.5 text-white" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <label
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-lifted-green text-black font-anton uppercase text-xs cursor-pointer hover:brightness-110 ${busy ? "opacity-60 cursor-wait" : ""}`}
          data-testid={`${testId}-picker`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {busy ? "Uploading…" : (value ? "Replace photo" : "Upload from device")}
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            className="hidden"
            disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`${testId}-file-input`}
          />
        </label>
        <button
          type="button"
          onClick={() => setShowUrlInput((s) => !s)}
          className="text-xs text-lifted-green hover:underline"
          data-testid={`${testId}-toggle-url`}
        >
          {showUrlInput ? "Hide URL input" : "Or paste a URL"}
        </button>
      </div>

      {showUrlInput && (
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://example.com/my-logo.png"
          className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:border-lifted-green"
          data-testid={`${testId}-url-input`}
        />
      )}

      {err && <div className="text-rose-300 text-xs" data-testid={`${testId}-err`}>{err}</div>}
      <p className="text-[10px] text-white/40">PNG / JPG / WEBP up to 5 MB. Square logos look best.</p>
    </div>
  );
}

